/**
 * Sentry initialization for the customer-facing React app.
 *
 * Required env vars (safe no-op when missing):
 *   VITE_SENTRY_DSN  – Sentry DSN for the mediloop-app project
 *
 * Source-map upload happens at build time via @sentry/vite-plugin
 * configured in vite.config.ts (set SENTRY_ORG, SENTRY_PROJECT_APP,
 * SENTRY_AUTH_TOKEN in CI / Vercel env).
 *
 * Mirrors the pattern in apps/web's @sentry/nextjs setup, but adapted
 * for Vite + React. Replay enabled to help reproduce client-side bugs
 * (the kind of save-failure UX issues that prompted this rollout).
 */

import * as Sentry from '@sentry/react';

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

/**
 * Initialize Sentry. Call once at app startup before render.
 * No-op when DSN is missing (local dev, preview deploys without env).
 */
export function initSentry(): void {
  if (!DSN) return;

  Sentry.init({
    dsn: DSN,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Customer-facing app touches PHI-adjacent content (bill amounts,
        // provider names). Mask aggressively in replays — we can debug from
        // structure + interactions without leaking specifics.
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,    // off by default — only on errors
    replaysOnErrorSampleRate: 1.0,

    enabled: import.meta.env.PROD,
  });
}

export { Sentry };
